'use client';

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLogging } from 'src/context/logging';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { discoveryApi } from 'src/api/redux';
import { SavedVolumeType } from './types';
import {
  setVolumeForLocation,
  selectVolumeForLocation
} from 'src/redux/slices/savedVolumesSlice';
import { RootState } from 'src/redux/reducers';

interface UseSavedVolumeReturn {
  appliedSavedVolume: SavedVolumeType | null;
  setSavedVolume: (volume: SavedVolumeType | null) => void;
}

/**
 * Custom hook for managing saved volume selection across different locations.
 * Handles state management, persistence via Redux, and synchronization of saved volumes.
 * Redux state automatically syncs to API via settingsListenerMiddleware.
 *
 * @param location - Optional location string. If not provided, uses currentLocation from user settings
 * @returns {UseSavedVolumeReturn} Object containing saved volume state and management function
 * @example
 * const { appliedSavedVolume, setSavedVolume } = useSavedVolume('discovery');
 */
export const useSavedVolume = (location?: string): UseSavedVolumeReturn => {
  const log = useLogging();
  const dispatch = useDispatch();
  const { settings: userSettings } = useUserSettings();
  const currentLocation = userSettings.currentLocation;

  // Use provided location or fall back to currentLocation
  const activeLocation = location || currentLocation;

  // Get saved volume from Redux store (single source of truth)
  const appliedSavedVolume = useSelector((state: RootState) =>
    activeLocation ? selectVolumeForLocation(state, activeLocation) : null
  );

  /**
   * Sets the saved volume for the current location and persists it via Redux.
   * Redux state automatically syncs to API via settingsListenerMiddleware with 1-second debounce.
   *
   * Side Effects:
   * - Dispatches Redux action (triggers automatic API sync via middleware)
   * - Invalidates RTK Query cache for discovery map/list to trigger re-fetch
   * - Rolls back Redux state on error to previous value
   *
   * Error Handling:
   * - Automatic rollback to previous state on failure
   * - Logs exception via useLogging for debugging
   *
   * @param volume - The volume to save, or null to clear the selection
   * @throws {Error} If no active location is set (logged, not thrown)
   */
  const setSavedVolume = useCallback(
    (volume: SavedVolumeType | null) => {
      if (!activeLocation) {
        log.exception(new Error('No active location for setSavedVolume'));
        return;
      }

      // Store current state for potential rollback
      const previousVolume = appliedSavedVolume;

      try {
        // Update Redux store (automatically syncs to API via middleware)
        dispatch(
          setVolumeForLocation({
            location: activeLocation,
            volume,
          })
        );

        // Invalidate discovery cache to ensure fresh state with new volume parameters
        dispatch(
          discoveryApi.util.invalidateTags([
            { type: 'Discovery', id: 'LIST' },
            { type: 'Discovery', id: 'MAP' },
          ])
        );
      } catch (error) {
        // Rollback on failure - restore previous Redux state
        dispatch(
          setVolumeForLocation({
            location: activeLocation,
            volume: previousVolume,
          })
        );
        log.exception(`Failed to save volume: ${error}`);
      }
    },
    [dispatch, activeLocation, appliedSavedVolume, log]
  );

  return {
    appliedSavedVolume,
    setSavedVolume,
  };
};

export default useSavedVolume;
